import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { apiUrl } from "../../config/api";
import { parseTwoColumnExcel, matchExcelItems } from "../../utils/excelImport";

/* Открытие карточки ТМЦ (при необходимости подправьте маршрут) */
const makeTmcUrl = (id) => `${window.location.origin}/tmc/${id}`;

const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const parseNum = (s) => {
  const n = parseFloat(String(s ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const EMPTY_AMORT_ROW = {
  tmc_id: null,
  name: "",
  code: "",
  unit: "",
  price: 0,
  qty: 0,
  total: 0,
  supplier_link: "",
  norm_months: 12,
  amort_per_month: 0,
};
const createEmptyRow = () => ({ ...EMPTY_AMORT_ROW });
const rowHasContent = (row = {}) => {
  if (!row) return false;
  if (String(row.name || "").trim()) return true;
  if (String(row.code || "").trim()) return true;
  if (String(row.unit || "").trim()) return true;
  if (Number(row.qty) > 0) return true;
  if (Number(row.price) > 0) return true;
  if (Number(row.norm_months) !== 12 && Number(row.norm_months) > 0) return true;
  if (row.supplier_link && row.supplier_link.trim()) return true;
  return false;
};

export default function AmortSheet({ data, onChange, readOnlyLocked = false }) {
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [draftRow, setDraftRow] = useState(createEmptyRow());
  const rowsWithDraft = useMemo(() => [...rows, { ...draftRow, _isDraft: true }], [rows, draftRow]);

  const [tmcDict, setTmcDict] = useState([]);
  const [groups, setGroups] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const loadDicts = () => {
    fetch(apiUrl('/api/tmc'))
      .then((r) => r.json())
      .then((arr) => (Array.isArray(arr) ? setTmcDict(arr) : setTmcDict([])))
      .catch(() => setTmcDict([]));

    fetch(apiUrl('/api/tmc/groups'))
      .then((r) => r.json())
      .then((arr) => (Array.isArray(arr) ? setGroups(arr) : setGroups([])))
      .catch(() => setGroups([]));
  };

  useEffect(() => {
    loadDicts();
    const onFocus = () => loadDicts();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const groupNameById = useMemo(() => {
    const map = new Map();
    for (const g of groups) map.set(String(g.id), String(g.name || ""));
    return map;
  }, [groups]);

  // Аморт-группа: убираем «Расходные материалы», оставляем «ОС/оборуд/инвентарь/инструмент/амортиз…»
  const isAmortizable = useCallback((item) => {
    const rawName = String(item?.groupName || item?.group || "");
    const gid = item?.group_id ?? item?.groupId ?? item?.groupID;
    const gname = gid != null ? String(groupNameById.get(String(gid)) || "") : rawName;
    const s = gname.toLowerCase();
    const isConsumable = s.includes("расходн");
    const looksAmort =
      s.includes("амортиз") || s.includes("основ") || s.includes("инвентар") || s.includes("оборуд") || s.includes("инструмент");
    return looksAmort && !isConsumable;
  }, [groupNameById]);

  const amortDict = useMemo(() => tmcDict.filter(isAmortizable), [tmcDict, isAmortizable]);

  // Пересчёт строки
  const recalcRow = (row) => {
    const qty = Number(row.qty) || 0;
    const price = Number(row.price) || 0;
    const total = round2(qty * price);

    const months = Math.max(1, Math.floor(Number(row.norm_months) || 0));
    const amortPerMonth = months > 0 ? round2(total / months) : 0;

    return { ...row, total, amort_per_month: amortPerMonth };
  };

  const applyRow = (idx, mutator, opts) => {
    if (readOnlyLocked) return;
    if (idx >= rows.length) {
      setDraftRow((prev) => recalcRow(mutator({ ...prev })));
      return;
    }
    const next = rows.map((r, i) => (i === idx ? recalcRow(mutator({ ...r })) : r));
    onChange(next, opts);
  };
  const applyAll = (mutator, opts) => {
    if (readOnlyLocked) return;
    const next = rows.map((r) => recalcRow(mutator({ ...r })));
    onChange(next, opts);
  };

  const commitDraftRow = useCallback(
    (opts) => {
      if (readOnlyLocked) return;
      if (!rowHasContent(draftRow)) return;
      const nextRow = recalcRow({ ...draftRow });
      onChange([...rows, nextRow], opts);
      setDraftRow(createEmptyRow());
    },
    [draftRow, onChange, readOnlyLocked, rows]
  );

  const handleDraftBlur = (event) => {
    if (readOnlyLocked) return;
    if (!rowHasContent(draftRow)) return;
    const stillInRow = event?.currentTarget?.contains(event?.relatedTarget);
    if (stillInRow) return;
    commitDraftRow({ forceCommit: true });
  };

  const removeRow = (idx) => {
    if (readOnlyLocked) return;
    onChange(rows.filter((_, i) => i !== idx), { forceCommit: true });
  };

  // helper: extract norm months from TMC item
  // IMPORTANT: the TMC card field "Амортизация (мес.)" is mapped to `amortization_period` in TmcCard.jsx.
  // We must prioritise that field and treat it as months (no years conversion).
  const extractNormMonths = (item) => {
    if (!item || typeof item !== "object") return 0;

    // 1) Primary field used in TmcCard.jsx
    if (item.hasOwnProperty("amortization_period")) {
      const v = Number(item.amortization_period);
      if (Number.isFinite(v) && v > 0) return Math.round(v);
    }

    // 2) Common alternate names that may also represent months; treat values as months directly
    const monthCandidates = ["norm_months", "amort_months", "amort_period", "amortization_months", "amort", "norm"];
    for (const key of monthCandidates) {
      if (item.hasOwnProperty(key)) {
        const v = Number(item[key]);
        if (Number.isFinite(v) && v > 0) return Math.round(v);
      }
    }

    // 3) Try inside nested props if present (less likely)
    const altFields = ["meta", "extra", "props", "attributes"];
    for (const af of altFields) {
      const obj = item[af];
      if (!obj || typeof obj !== "object") continue;
      for (const key in obj) {
        const maybe = Number(obj[key]);
        if (Number.isFinite(maybe) && maybe > 0) {
          return Math.round(maybe);
        }
      }
    }

    return 0;
  };

  const onPickTmc = (idx, item) => {
    if (!item) {
      applyRow(idx, (row) => ({
        ...row,
        tmc_id: null,
        name: "",
        code: "",
        unit: "",
        price: 0,
        supplier_link: "",
      }));
      return;
    }

    const normFromItem = extractNormMonths(item);

    applyRow(idx, (row) => {
      const nextPrice = Number(item.price) || 0;
      return {
        ...row,
        tmc_id: item.id,
        name: item.name ?? "",
        code: item.code ?? "",
        unit: item.unit ?? "",
        price: nextPrice,
        supplier_link: item.supplier_link ?? "",
        // if the TMC contains an amortization_period (months) — use it; otherwise keep existing or default 12
        norm_months: normFromItem > 0 ? normFromItem : (row.norm_months ?? 12),
      };
    });
  };

  const refreshPrices = () => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const byId = new Map();
    const byCode = new Map();
    for (const d of tmcDict) {
      if (d?.id !== undefined) byId.set(String(d.id), d);
      if (d?.code !== undefined) byCode.set(String(d.code), d);
    }
    applyAll((row) => {
      let dictItem = null;
      if (row.tmc_id != null) dictItem = byId.get(String(row.tmc_id));
      if (!dictItem && row.code != null) dictItem = byCode.get(String(row.code));
      if (!dictItem) return row;
      const newPrice = Number(dictItem.price) || 0;
      const normFromDict = extractNormMonths(dictItem);
      return {
        ...row,
        price: newPrice,
        norm_months: normFromDict > 0 ? normFromDict : row.norm_months,
      };
    }, { forceCommit: true });
  };

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.totalG += Number(r.total) || 0;
          acc.totalJ += Number(r.amort_per_month) || 0;
          return acc;
        },
        { totalG: 0, totalJ: 0 }
      ),
    [rows]
  );

  // Стили
  const tableStyle = {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100%",
    tableLayout: "auto",
    fontSize: "0.92rem",
  };
  const cellBase = {
    border: "1px solid #dfe3eb",
    padding: "6px 4px",
    fontSize: "0.9rem",
    background: "transparent",
    verticalAlign: "middle",
    overflow: "visible",
    whiteSpace: "normal",
    lineHeight: 1.25,
  };
  const thGray = { ...cellBase, background: "#f5f5f7", fontWeight: "bold", textAlign: "center" };
  const roGray = { ...cellBase, textAlign: "left" };
  const roGrayCenter = { ...cellBase, textAlign: "center" };
  const roGrayRight = { ...cellBase, textAlign: "right" };
  const editWhiteLeftWrap = { ...cellBase, textAlign: "left" };
  const editWhiteRight = { ...cellBase, textAlign: "right" };
  const thHeaderStack = { display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "center", gap: 4 };
  const headerBtn = {
    display: "inline-block",
    marginTop: 4,
    padding: "4px 6px",
    fontSize: 12,
    border: "1px solid #c59bff",
    background: "#f5efff",
    color: "#5427b0",
    borderRadius: 6,
    cursor: "pointer",
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    alignSelf: "stretch",
  };
  const inputNum = {
    width: "100%",
    padding: "6px 2px 4px",
    border: "none",
    borderBottom: "1px solid rgba(84,39,176,0.3)",
    textAlign: "right",
    boxSizing: "border-box",
    fontVariantNumeric: "tabular-nums",
    fontSize: "0.92rem",
    background: "transparent",
  };
  const disabledInput = {
    ...inputNum,
    borderBottom: "1px solid #dfe3eb",
    color: "#a3aab7",
    cursor: "not-allowed",
  };
  const editLink = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    minWidth: 28,
    height: 28,
    border: "1px solid #c59bff",
    borderRadius: 6,
    color: "#5427b0",
    background: "#f5efff",
    cursor: "pointer",
    textDecoration: "none",
    fontWeight: 700,
  };
  const deleteBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    minWidth: 28,
    height: 28,
    border: "1px solid #f5c6cb",
    borderRadius: 6,
    color: "#b91c1c",
    background: "#fff5f6",
    cursor: "pointer",
    marginLeft: 6,
    fontWeight: 700,
  };

  const handleCreateTmc = () => {
    if (typeof window === "undefined") return;
    const url = `${window.location.origin}/tmc/new`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const triggerExcelUpload = () => {
    if (readOnlyLocked) return;
    fileInputRef.current?.click();
  };

  const handleExcelImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || typeof onChange !== "function") return;
    setImporting(true);
    try {
      const excelItems = await parseTwoColumnExcel(file);
      const { matched, missing } = matchExcelItems(excelItems, amortDict);
      if (!matched.length) {
        alert("Не найдено совпадений в справочнике амортизируемых ТМЦ.");
        return;
      }
      let base = rows;
      if (rows.length > 0) {
        const replace = window.confirm(
          "Очистить текущий список амортизации перед импортом? Нажмите Отмена, чтобы добавить строки в конец."
        );
        base = replace ? [] : rows.slice();
      } else {
        base = [];
      }

      const nextRows = base.concat(
        matched.map(({ excel, dict }) => {
          const norm = extractNormMonths(dict) || 12;
          return recalcRow({
            tmc_id: dict.id ?? null,
            name: dict.name ?? excel.originalName,
            code: dict.code ?? "",
            unit: dict.unit ?? "",
            price: Number(dict.price) || 0,
            qty: excel.quantity,
            total: 0,
            supplier_link: dict.supplier_link ?? "",
            norm_months: norm,
            amort_per_month: 0,
          });
        })
      );

      onChange(nextRows, { forceCommit: true });
      setDraftRow(createEmptyRow());

      let message = `Импортировано строк: ${matched.length}.`;
      if (missing.length) {
        const preview = missing.slice(0, 10).join(", ");
        message += `\nНе найдены (${missing.length}): ${preview}${missing.length > 10 ? "…" : ""}`;
      }
      alert(message);
    } catch (err) {
      alert(err?.message || "Ошибка импорта Excel");
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteAll = () => {
    if (readOnlyLocked || !rows.length) return;
    const ok = window.confirm("Удалить все позиции амортизации на этой странице?");
    if (!ok) return;
    onChange([], { forceCommit: true });
    setDraftRow(createEmptyRow());
  };

  const exportToExcel = () => {
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const title = "Карточка расчёта амортизации";
    const generatedAt = `Сформировано: ${new Date().toLocaleString('ru-RU')}`;
    const headers = [
      "№ п/п",
      "Наименование",
      "Код",
      "Кол-во",
      "Ед. изм.",
      "Цена за ед.",
      "Итого, руб. с НДС",
      "Ссылка",
      "Норма амортизации, месяцев",
      "Сумма амортизации в месяц, руб.",
    ];

    const bodyRows = rows.map((row, idx) => [
      idx + 1,
      row.name || "",
      row.code || "",
      Number(row.qty) || 0,
      row.unit || "",
      Number(row.price) || 0,
      Number(row.total) || 0,
      row.supplier_link || "",
      Number(row.norm_months) || 0,
      Number(row.amort_per_month) || 0,
    ]);

    const totalsRow = [
      "",
      "ИТОГО",
      "",
      rows.reduce((sum, row) => sum + (Number(row.qty) || 0), 0),
      "",
      "",
      totals.totalG || 0,
      "",
      "",
      totals.totalJ || 0,
    ];

    const sheetData = [[title], [generatedAt], [], headers, ...bodyRows, totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
    ];
    ws["!cols"] = [
      8, 34, 16, 11, 12, 14, 16, 28, 16, 18,
    ].map((wch) => ({ wch }));

    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let row = range.s.r; row <= range.e.r; row += 1) {
      for (let col = range.s.c; col <= range.e.c; col += 1) {
        const cell = ws[XLSX.utils.encode_cell({ r: row, c: col })];
        if (!cell) continue;
        cell.s = cell.s || {};
        cell.s.border = {
          top: { style: "thin", color: { rgb: "D9E2F2" } },
          bottom: { style: "thin", color: { rgb: "D9E2F2" } },
          left: { style: "thin", color: { rgb: "D9E2F2" } },
          right: { style: "thin", color: { rgb: "D9E2F2" } },
        };
        cell.s.alignment = { vertical: "center", horizontal: col === 1 || col === 2 || col === 4 || col === 7 ? "left" : col === 0 ? "center" : "right", wrapText: true };
        if (row === 0) {
          cell.s.font = { bold: true, color: { rgb: "FFFFFF" }, sz: 14 };
          cell.s.fill = { patternType: "solid", fgColor: { rgb: "1D4ED8" } };
          cell.s.alignment = { vertical: "center", horizontal: "center", wrapText: true };
        } else if (row === 1) {
          cell.s.font = { italic: true, color: { rgb: "475569" } };
          cell.s.fill = { patternType: "solid", fgColor: { rgb: "F8FAFC" } };
          cell.s.alignment = { vertical: "center", horizontal: "center", wrapText: true };
        } else if (row === 3) {
          cell.s.font = { bold: true, color: { rgb: "0F172A" } };
          cell.s.fill = { patternType: "solid", fgColor: { rgb: "E2E8F0" } };
          cell.s.alignment = { vertical: "center", horizontal: "center", wrapText: true };
        } else if (row === range.e.r) {
          cell.s.font = { bold: true, color: { rgb: "0F172A" } };
          cell.s.fill = { patternType: "solid", fgColor: { rgb: "DCFCE7" } };
        }

        if (row >= 4 && row < range.e.r) {
          if ([3, 8].includes(col)) cell.z = "0.000";
          if ([5, 6, 9].includes(col)) cell.z = '#,##0.00';
        }
        if (row === range.e.r) {
          if ([3, 8].includes(col)) cell.z = "0.000";
          if ([5, 6, 9].includes(col)) cell.z = '#,##0.00';
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Амортизация");
    XLSX.writeFile(wb, `amort_card_${stamp}.xlsx`, { cellStyles: true });
  };

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <button
            type="button"
            onClick={handleCreateTmc}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #93c5fd",
              background: "#e0ecff",
              color: "#1d4ed8",
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
              opacity: readOnlyLocked ? 0.6 : 1,
              pointerEvents: readOnlyLocked ? "none" : "auto",
            }}
          >
            Создать новую позицию ТМЦ
          </button>

          <button
            type="button"
            onClick={handleDeleteAll}
            disabled={readOnlyLocked || !rows.length}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #ef4444",
              background: "#fee2e2",
              color: "#b91c1c",
              fontWeight: 700,
              cursor: readOnlyLocked || !rows.length ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              opacity: readOnlyLocked || !rows.length ? 0.6 : 1,
            }}
          >
            Удалить все
          </button>

          <button
            type="button"
            onClick={exportToExcel}
            disabled={!rows.length}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #16a34a",
              background: "#dcfce7",
              color: "#166534",
              fontWeight: 700,
              cursor: !rows.length ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              opacity: !rows.length ? 0.6 : 1,
            }}
          >
            Выгрузить в Excel
          </button>

          <button
            type="button"
            onClick={triggerExcelUpload}
            disabled={readOnlyLocked || importing}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #0ea5e9",
              background: "#e0f2fe",
              color: "#0f172a",
              fontWeight: 700,
              cursor: readOnlyLocked ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              opacity: readOnlyLocked ? 0.6 : 1,
            }}
          >
            {importing ? "Импорт…" : "Загрузить из Excel"}
          </button>
        </div>

        <span style={{ fontSize: 13, color: "#6b7280" }}>
          Последняя строка всегда пустая — начните ввод, и она сохранится автоматически.
        </span>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleExcelImport}
          disabled={readOnlyLocked || importing}
        />
      </div>

      <table style={tableStyle}>

        <thead>
          <tr>
            <th style={thGray}>№ п/п</th>
            <th style={thGray}>наименование</th>
            <th style={thGray}>Код</th>
            <th style={thGray}>Кол-во</th>
            <th style={thGray}>Ед. изм.</th>
            <th style={thGray}>
              <div style={thHeaderStack}>
                <div>Цена за ед.</div>
                <button type="button" onClick={refreshPrices} style={headerBtn} title="Подтянуть актуальные цены из справочника">
                  Обновить цены
                </button>
              </div>
            </th>
            <th style={thGray}>Итого, руб. с НДС</th>
            <th style={thGray}>Ссылка</th>
            <th style={thGray}>Норма амортизации, месяцев</th>
            <th style={thGray}>Сумма амортизации в месяц, руб.</th>
            <th style={thGray}>Действия</th>
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={11} style={{ ...roGray, textAlign: "center", color: "#999", whiteSpace: "normal" }}>
                Нет позиций. Заполните строку ниже — она сохранится автоматически.
              </td>
            </tr>
          )}

          {rowsWithDraft.map((row, idx) => {
            const isDraft = idx === rows.length;
            const draftEmpty = isDraft && !rowHasContent(row);
            const rowKey = isDraft ? "amort-draft" : idx;
            return (
              <tr key={rowKey} onBlurCapture={isDraft ? handleDraftBlur : undefined}>
                <td style={isDraft ? { ...roGrayCenter, color: "#a3aab7" } : roGrayCenter}>{idx + 1}</td>

                <td style={editWhiteLeftWrap}>
                  <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <NamePicker
                        value={row.name || ""}
                        dict={amortDict}
                        disabled={readOnlyLocked}
                        onPick={(item) => onPickTmc(idx, item)}
                        onClear={() => onPickTmc(idx, null)}
                      />
                    </div>
                    {row.tmc_id ? (
                      <a
                        href={makeTmcUrl(row.tmc_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Открыть карточку ТМЦ в новой вкладке"
                        style={editLink}
                      >
                        ↗
                      </a>
                    ) : null}
                  </div>
                </td>

                <td style={isDraft ? { ...roGrayCenter, color: "#a3aab7" } : roGrayCenter}>{row.code || ""}</td>

                <td style={editWhiteRight}>
                  <input
                    type="text"
                    value={row.qty ?? ""}
                    disabled={readOnlyLocked}
                    inputMode="decimal"
                    pattern="[0-9., ]*"
                    onChange={(e) => applyRow(idx, (r) => ({ ...r, qty: parseNum(e.target.value) }))}
                    style={readOnlyLocked ? disabledInput : inputNum}
                  />
                </td>

                <td style={isDraft ? { ...roGrayCenter, color: "#a3aab7" } : roGrayCenter}>{row.unit || ""}</td>

                <td style={isDraft ? { ...roGrayRight, color: "#a3aab7" } : roGrayRight}>{draftEmpty ? "—" : fmtMoney(row.price)}</td>
                <td style={isDraft ? { ...roGrayRight, color: "#a3aab7" } : roGrayRight}>{draftEmpty ? "—" : fmtMoney(row.total)}</td>

                <td style={{ ...roGray, whiteSpace: "normal", wordBreak: "break-all", ...(draftEmpty ? { color: "#a3aab7" } : null) }}>
                  {row.supplier_link ? (
                    <a href={row.supplier_link} target="_blank" rel="noopener noreferrer" style={{ color: "#3a27c5", textDecoration: "underline" }} title={row.supplier_link}>
                      Ссылка
                    </a>
                  ) : (
                    "—"
                  )}
                </td>

                <td style={isDraft ? { ...roGrayCenter, color: "#a3aab7" } : roGrayCenter}>
                  {row.norm_months != null && row.norm_months !== "" ? row.norm_months : "—"}
                </td>

                <td style={isDraft ? { ...roGrayRight, color: "#a3aab7" } : roGrayRight}>{draftEmpty ? "—" : fmtMoney(row.amort_per_month)}</td>

                <td style={roGrayCenter}>
                  {!isDraft && (
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      title="Удалить строку"
                      style={{ ...deleteBtnStyle, width: "100%", minWidth: 0, opacity: readOnlyLocked ? 0.4 : 1, cursor: readOnlyLocked ? "not-allowed" : "pointer" }}
                      disabled={readOnlyLocked}
                    >
                      Удалить
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>

        {rows.length > 0 && (
          <tfoot>
            <tr>
              <td style={roGray} />
              <td style={{ ...roGray, fontWeight: 800, whiteSpace: "normal" }}>ИТОГО</td>
              <td style={roGray} />
              <td style={roGray} />
              <td style={roGray} />
              <td style={roGray} />
              <td style={{ ...roGrayRight, fontWeight: 800 }}>{fmtMoney(totals.totalG)}</td>
              <td style={roGray} />
              <td style={roGray} />
              <td style={{ ...roGrayRight, fontWeight: 800 }}>{fmtMoney(totals.totalJ)}</td>
              <td style={roGray} />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/* ИМЕННО ЗДЕСЬ: Расширенный дропдаун с авто-подгонкой ширины, переносами и отступом слева */
function NamePicker({ value, dict, onPick, onClear, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const anchorRef = useRef(null);
  const dropdownRef = useRef(null);
  const [ddStyle, setDdStyle] = useState({ top: 0, left: 0, width: 0, maxHeight: 420 });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = Array.isArray(dict) ? dict : [];
    if (!s) return base.slice(0, 300);
    return base
      .filter(
        (d) =>
          String(d.name || "").toLowerCase().includes(s) ||
          String(d.code || "").toLowerCase().includes(s)
      )
      .slice(0, 300);
  }, [dict, q]);

  const positionDropdown = useCallback(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();

    const margin = 16;
    const leftOffset = 14;
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;
    const minWidth = Math.min(720, vpW - margin * 2);
    const desiredWidth = Math.max(r.width, minWidth);
    const maxLeft = vpW - desiredWidth - margin;
    const baseLeft = r.left + leftOffset;
    const leftPx = Math.max(margin, Math.min(baseLeft, maxLeft));

    const dropdownEl = dropdownRef.current;
    const estimatedHeight = dropdownEl?.offsetHeight || 320;
    const viewportAllowance = Math.max(200, Math.min(420, vpH - margin * 2));
    const maxHeight = viewportAllowance;
    const usedHeight = Math.min(estimatedHeight, viewportAllowance);

    const availableBottom = vpH - r.bottom - margin;
    const availableTop = r.top - margin;
    const shouldOpenUp = availableBottom < usedHeight && availableTop > availableBottom;
    const baseTop = shouldOpenUp ? r.top - usedHeight : r.bottom;
    const clampedTop = Math.max(margin, Math.min(baseTop, vpH - margin - usedHeight));

    setDdStyle({
      top: Math.round(clampedTop),
      left: Math.round(leftPx),
      width: Math.round(desiredWidth),
      maxHeight: Math.round(maxHeight),
    });
  }, []);

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((prev) => {
      const next = !prev;
      if (!next) return next;
      positionDropdown();
      requestAnimationFrame(positionDropdown);
      setTimeout(positionDropdown, 120);
      return next;
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    positionDropdown();
    const raf = requestAnimationFrame(positionDropdown);
    const timeout = setTimeout(positionDropdown, 120);
    const onWin = () => positionDropdown();
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("resize", onWin, true);
    const onDoc = (e) => {
      const a = anchorRef.current;
      const d = dropdownRef.current;
      if ((a && a.contains(e.target)) || (d && d.contains(e.target))) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc, true);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("resize", onWin, true);
      document.removeEventListener("mousedown", onDoc, true);
    };
  }, [open, positionDropdown]);

  useEffect(() => {
    if (open) positionDropdown();
  }, [open, filtered.length, q, positionDropdown]);

  return (
    <div style={{ position: "relative", width: "98%" }}>
      <button
        ref={anchorRef}
        type="button"
        onClick={toggleOpen}
        title={value}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "6px 0",
          border: "none",
          borderBottom: disabled ? "1px solid #dfe3eb" : "1px solid rgba(84,39,176,0.3)",
          borderRadius: 0,
          textAlign: "left",
          background: "transparent",
          cursor: disabled ? "not-allowed" : "pointer",
          minHeight: 30,
        }}
      >
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.25, color: value ? "#111" : "#999" }}>
          {value || "— выберите позицию из справочника —"}
        </div>
      </button>

      {open && !disabled &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: ddStyle.top,
              left: ddStyle.left,
              width: ddStyle.width,
              maxHeight: ddStyle.maxHeight || 420,
              overflowY: "auto",
              overflowX: "hidden",
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,.2)",
              zIndex: 9999,
            }}
          >
            <div style={{ padding: 8, borderBottom: "1px solid #eee", display: "flex", gap: 8 }}>
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Поиск по названию или коду…"
                style={{ flex: 1, padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6 }}
              />
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setQ("");
                  onClear?.();
                  setOpen(false);
                }}
                style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 6, background: "#f5f5f5", cursor: "pointer", whiteSpace: "nowrap" }}
                title="Очистить"
              >
                Очистить
              </button>
            </div>

            <div>
              {filtered.length === 0 && <div style={{ padding: 10, color: "#777" }}>Ничего не найдено</div>}
              {filtered.map((d) => (
                <div
                  key={d.id}
                  role="button"
                  tabIndex={0}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onPick(d);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    textAlign: "left",
                    borderBottom: "1px solid #f2f2f2",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                  title={d.name}
                >
                  <div style={{ fontWeight: 700, lineHeight: 1.25, whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {d.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 4, whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    Код: {d.code ?? "—"} • Ед.: {d.unit ?? "—"} • Цена: {fmtMoney(d.price)}
                  </div>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}